import React from "react";
import { Link } from "react-router-dom";

const FIELD_LABELS = {
  subweight: "Subweight",
  ptAssay: "Pt Assay",
  pdAssay: "Pd Assay",
  rhAssay: "Rh Assay",
};

const BUCKET_ORDER = ["0-5", "5-20", "20-50", "above50"];

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function AssayQualityBuckets({ summary, selectedField, selectedBucket, onSelect, onClear }) {
  const qualityByField = summary?.qualityByField || [];
  if (!qualityByField.length) {
    return <div className="c-gray fs-14">No assay quality data available.</div>;
  }

  return (
    <div className="converter-testing-drift-buckets">
      <div className="converter-testing-executive-title">Variability by assay field</div>
      <p className="converter-testing-drift-help c-gray fs-13 mb-16">
        Variability % = std dev ÷ test average × 100 (coefficient of variation). Converters need 2+ tests.
        Click a bucket to list OEMs.
      </p>
      {qualityByField.map((fieldRow) => (
        <div key={fieldRow.field} className="converter-testing-drift-field-block">
          <div className="converter-testing-drift-field-title">{fieldRow.label || FIELD_LABELS[fieldRow.field]}</div>
          <div className="converter-testing-drift-bucket-row">
            {BUCKET_ORDER.map((bucketId) => {
              const bucket = (fieldRow.buckets || []).find((b) => b.id === bucketId);
              const isActive =
                selectedField === fieldRow.field && selectedBucket === bucketId;
              return (
                <button
                  key={`${fieldRow.field}-${bucketId}`}
                  type="button"
                  className={`converter-testing-drift-bucket ${isActive ? "active" : ""}`}
                  onClick={() => onSelect(fieldRow.field, bucketId)}
                >
                  <div className="converter-testing-drift-bucket-label">
                    {bucket?.label || bucketId}
                  </div>
                  <div className="converter-testing-drift-bucket-count">
                    {(bucket?.count ?? 0).toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selectedField && selectedBucket && (
        <button type="button" className="converter-testing-details-toggle" onClick={onClear}>
          ✕ Clear selection
        </button>
      )}
    </div>
  );
}

export function AssayQualityOemList({
  items,
  qualityField,
  totalCount,
  offset,
  areMoreItemsAvailable,
  onPrev,
  onNext,
}) {
  if (!qualityField) return null;

  const label = FIELD_LABELS[qualityField] || qualityField;
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = offset + items.length;

  return (
    <div className="converter-testing-drift-oem-list">
      <div className="c-red fw-bolder mb-10 fs-14">
        OEMs — {label} variability
      </div>
      <div className="converter-testing-details-meta">
        Showing {from}–{to} of {totalCount.toLocaleString()} converters
      </div>
      {!items.length ? (
        <div className="c-gray fs-14">No converters in this bucket.</div>
      ) : (
        <div className="converter-testing-drift-wrap">
          <table className="converter-testing-drift-table">
            <thead>
              <tr>
                <th>OEM</th>
                <th>Type</th>
                <th>Test avg</th>
                <th>Std dev</th>
                <th>Variability %</th>
                <th>Tests</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const testAvg = item.testAvg?.[qualityField];
                const stdDev = item.stdDev?.[qualityField];
                const cvPct = item.cvPct?.[qualityField];
                return (
                  <tr key={item.productId}>
                    <td>
                      <Link className="converter-testing-link" to={`/converter/${item.productId}`}>
                        {item.oem || item.productId}
                      </Link>
                    </td>
                    <td>{item.type || "—"}</td>
                    <td>{formatNumber(testAvg)}</td>
                    <td>{formatNumber(stdDev)}</td>
                    <td className="fw-bolder">{cvPct != null ? `${cvPct}%` : "—"}</td>
                    <td>{item.testCount ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {items.length > 0 && (
        <div className="converter-testing-pagination">
          <button
            type="button"
            className="converter-testing-page-btn converter-testing-page-btn-prev"
            disabled={offset === 0}
            onClick={onPrev}
          >
            Previous
          </button>
          <button
            type="button"
            className="converter-testing-page-btn converter-testing-page-btn-next"
            disabled={!areMoreItemsAvailable}
            onClick={onNext}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
